import type { ActivityDosage, Cadence, GoalTarget } from '@eluve/plan-shapes';

const PERIOD_UNIT_LABEL = { d: 'day', wk: 'week', mo: 'month' } as const;

// "2× / week", "1× / 3 months"
export function formatCadence(cadence: Cadence): string {
  const unit = PERIOD_UNIT_LABEL[cadence.periodUnit];
  const period =
    cadence.period && cadence.period > 1 ? `${cadence.period} ${unit}s` : unit;
  return `${cadence.frequency}× / ${period}`;
}

export function formatDuration(days: number): string {
  if (days % 7 === 0) {
    const weeks = days / 7;
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  return days === 1 ? '1 day' : `${days} days`;
}

// Dosage as chips: ["3 × 10", "45s hold", "yellow band"]
export function formatDosage(dosage: ActivityDosage | undefined): string[] {
  if (!dosage) {
    return [];
  }
  const parts: string[] = [];
  if (dosage.sets !== undefined && dosage.reps !== undefined) {
    parts.push(`${dosage.sets} × ${dosage.reps}`);
  } else if (dosage.sets !== undefined) {
    parts.push(`${dosage.sets} sets`);
  } else if (dosage.reps !== undefined) {
    parts.push(`${dosage.reps} reps`);
  }
  if (dosage.holdSeconds !== undefined) {
    parts.push(`${dosage.holdSeconds}s hold`);
  }
  if (dosage.resistance) {
    parts.push(dosage.resistance);
  }
  return parts;
}

// "0–2", "≤ 10 %", "≥ 20 seconds", "25 repetitions"
export function formatTargetValue(
  target: Pick<GoalTarget, 'value' | 'low' | 'high' | 'unit'>,
): string {
  const unit = target.unit ? ` ${target.unit}` : '';
  if (target.low !== undefined && target.high !== undefined) {
    return `${target.low}–${target.high}${unit}`;
  }
  if (target.high !== undefined) {
    return `≤ ${target.high}${unit}`;
  }
  if (target.low !== undefined) {
    return `≥ ${target.low}${unit}`;
  }
  if (target.value !== undefined) {
    return `${target.value}${unit}`;
  }
  return '—';
}
